import {
  ActivitiesPageFilters,
  DurationFilter,
  i18nKey,
} from "domain/entities/activities.entities";
import {
  SeanceTechnique,
  Technique,
  TechniqueContext,
} from "domain/entities/techniques.entities";
import { selectIsSeanceLoading } from "domain/seances/seances.selectors";
import { getTechniques } from "domain/techniques/techniques.actions";
import { selectTechniques } from "domain/techniques/techniques.selectors";
import { useEffect, useState } from "react";
import { HiAdjustments, HiSparkles } from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { DesktopActivitiesFilters } from "ui/pages/app/components/activities/DesktopActivitiesFilters";
import { MobileActivitiesFilters } from "ui/pages/app/components/activities/MobileActivitiesFilters";
import { TechniqueCard } from "ui/pages/app/components/activities/techniques/TechniqueCard";
import { BackButton } from "ui/pages/app/components/buttons/BackButton";
import { INSkeleton } from "ui/pages/app/components/inputs/INSkeleton";
import { useWindowWidth } from "ui/utils/hooks";
import { SelectionZone } from "ui/pages/app/seances/create-seance/components/SelectionZone";
import { Modal, ModalContent } from "@chakra-ui/modal";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { ModalTechnique } from "ui/pages/app/seances/create-seance/steps/add-content/modals/ModalTechnique";
import { useDisclosure } from "@chakra-ui/hooks";
import { INSearchField } from "ui/pages/app/components/inputs/INSearchField";
import { getFiltersCount } from "ui/utils/get-filters-count";
import { v4 as uuid } from "uuid";
import { selectFavoriteTechniques } from "domain/activities/activities.selectors";
import { getFavoriteActivities } from "domain/activities/activities.actions";
import { ModalEditTechnique } from "ui/pages/app/seances/create-seance/steps/add-content/modals/ModalEditTechnique";
import { NoResultFound } from "ui/assets/svg/NoResultFound";
import { Trans } from "react-i18next";
import { AISearchButton } from "ui/pages/app/components/activities/AI/AISearchButton";
import { ModalAISearch } from "ui/pages/app/components/activities/AI/ModalAISearch";
import { clearTechniqueFeedbackValues } from "domain/ai-search/ai-search.actions";
import { AISearchTooltip } from "ui/pages/app/components/activities/AI/AISearchTooltip";
import {
  selectIsSearchingTechnique,
  selectTechniquesFromAISearch,
} from "domain/ai-search/ai-search.selectors";

interface IProps {
  selectedDuration: number;
  isOpen: boolean;
  onClose: () => void;
  context: TechniqueContext;
  participants: number;
  modality: string;
  currentDuration: number;
  onOpen: () => void;
  addTechniques: (
    techniques: SeanceTechnique[],
    context: TechniqueContext,
    order: number
  ) => Promise<{
    success: boolean;
  }>;
}

export function ModalChooseTechniques({
  selectedDuration,
  isOpen,
  onClose,
  context,
  participants,
  modality,
  currentDuration,
  onOpen,
  addTechniques,
}: IProps) {
  const isLoading = useAppSelector(selectIsSeanceLoading);
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const aiTechniqueRefs = useAppSelector(selectTechniquesFromAISearch);
  const techniques = useAppSelector(selectTechniques).filter((technique) =>
    aiTechniqueRefs.length === 0
      ? true
      : aiTechniqueRefs.includes(technique.innizRef)
  );
  const favoriteTechniques = useAppSelector(selectFavoriteTechniques).map(
    (tech) => tech.techniqueId
  );
  const isSearching = useAppSelector(selectIsSearchingTechnique);
  const modalViewTechnique = useDisclosure();
  const modalEditTechnique = useDisclosure();
  const modalAISearch = useDisclosure();
  const minDuration = 0;
  const maxDuration = 145;
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSelectionZoneOpen, setIsSelectionZoneOpen] = useState(false);
  const [selectedTechniques, setSelectedTechniques] = useState<Technique[]>([]);
  const [techniqueToView, setTechniqueToView] = useState<Technique | null>();
  const [search, setSearch] = useState("");
  const [favoriteFilter, setFavoriteFilter] = useState(false);
  const [filters, setFilters] = useState<
    ActivitiesPageFilters & {
      [key: string]: string[] | string | boolean | number | DurationFilter;
    }
  >({
    contexts: [context],
    types: [],
    techObjectives: [],
    participants: "",
    modalities: [],
    level: [],
    duration: {
      $gte: minDuration,
      $lte: maxDuration,
    },
  });

  useEffect(() => {
    setFilters({
      contexts: [context],
      types: [],
      techObjectives: [],
      participants: participants.toString(),
      modalities: [modality],
      level: [],
      duration: {
        $gte: minDuration,
        $lte: maxDuration,
      },
      favorites: false,
    });
  }, [context, participants, modality, isOpen]); //eslint-disable-line

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchTechniques();
    }, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [filters, context, search]); //eslint-disable-line

  useEffect(() => {
    dispatch(getFavoriteActivities());
    return () => {
      dispatch(clearTechniqueFeedbackValues());
    };
  }, []); //eslint-disable-line

  const filteredTechniques = favoriteFilter
    ? techniques.filter((t) => favoriteTechniques.includes(t.id))
    : techniques;

  const fetchTechniques = () => {
    dispatch(
      getTechniques({
        pauses: false,
        search,
        isInniz: true,
        ...filters,
      })
    );
  };

  const updateSelectedTechniques = (
    technique: Technique,
    action: "add" | "remove"
  ) => {
    if (action === "remove") {
      if (technique.tempId) {
        setSelectedTechniques([
          ...selectedTechniques.filter((t) => t.tempId !== technique.tempId),
        ]);
      } else {
        setSelectedTechniques([
          ...selectedTechniques.filter((t) => t.id !== technique.id),
        ]);
      }
    } else if (action === "add") {
      setSelectedTechniques([
        ...selectedTechniques,
        { ...technique, tempId: uuid() },
      ]);
    }
  };

  const addTechniquesToSeance = async () => {
    const result = await addTechniques(
      selectedTechniques.map((technique) => {
        return {
          technique,
        };
      }),
      context,
      selectedTechniques.length
    );
    if (result.success) {
      setSelectedTechniques([]);
      onClose();
    }
  };

  const closeViewTechniqueModal = () => {
    modalViewTechnique.onClose();
    onOpen();
  };

  const closeEditTechniqueModal = (openView: boolean) => {
    modalEditTechnique.onClose();
    if (openView) modalViewTechnique.onOpen();
    else onOpen();
  };

  const openViewTechniqueModal = (technique: Technique) => {
    setTechniqueToView(technique);
    onClose();
    modalViewTechnique.onOpen();
  };

  const openEditTechniqueModal = () => {
    modalViewTechnique.onClose();
    modalEditTechnique.onOpen();
  };

  const resetFilters = () => {
    setFilters({
      contexts: [],
      types: [],
      techObjectives: [],
      participants: "",
      modalities: [],
      duration: {
        $gte: minDuration,
        $lte: maxDuration,
      },
    });
    setFavoriteFilter(false);
  };

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalContent>
          <div className="relative h-dvh w-full overflow-x-hidden bg-darkGrey-100">
            <div
              className={`flex min-h-dvh flex-row overflow-x-hidden ${
                isFiltersOpen ? "filters-open" : "filters-closed"
              }`}
              style={{ width: isDesktop ? "100%" : "200vw" }}
            >
              <div className="w-full px-2 py-4 md:px-12 md:py-9">
                <div className="flex h-10 w-full items-center justify-between px-2 md:px-0">
                  <BackButton onClick={onClose} hideText={!isDesktop} />
                </div>

                <div className="mt-8 flex w-full justify-center">
                  <h4 className="w-fit text-center font-bold text-secondaryGrey-900">
                    {translate(`ns1:CreateSeance.SelectTechniques.${context}`)}
                  </h4>
                </div>

                {isDesktop ? (
                  <div className="w-fit justify-self-center">
                    <div className="mt-8 flex">
                      <INSearchField
                        placeholder={translate(
                          "ns1:Input.Placeholders.TechniqueName"
                        )}
                        value={search}
                        setValue={setSearch}
                        width="100%"
                      />
                      <AISearchTooltip>
                        <AISearchButton
                          className="ms-3 !px-12"
                          onClick={modalAISearch.onOpen}
                        />
                      </AISearchTooltip>
                    </div>
                    <DesktopActivitiesFilters
                      filters={filters}
                      setFilters={setFilters}
                      fetchActivities={fetchTechniques}
                      activitiesType="techniques"
                      minDuration={minDuration}
                      maxDuration={maxDuration}
                      className="!mt-4"
                      resetFilters={resetFilters}
                      favoriteFilter={favoriteFilter}
                      setFavoriteFilter={setFavoriteFilter}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mt-7 flex">
                      <INSearchField
                        placeholder={translate(
                          "ns1:Input.Placeholders.TechniqueName"
                        )}
                        value={search}
                        setValue={setSearch}
                        width="100%"
                        height="48px"
                      />
                      <AISearchTooltip>
                        <AISearchButton
                          className="ms-3 !px-3"
                          onClick={modalAISearch.onOpen}
                        />
                      </AISearchTooltip>
                    </div>
                    <button
                      className="mt-3 flex w-full items-center justify-center rounded-xl border border-darkGrey-200 bg-white py-2 text-base font-bold"
                      onClick={() => setIsFiltersOpen(true)}
                    >
                      <HiAdjustments
                        color="#2B3674"
                        size="20"
                        className="me-1.5"
                      />
                      {translate("ns1:Button.Filters")}
                      <span className="ms-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs text-white">
                        {getFiltersCount(filters)}
                      </span>
                    </button>
                  </>
                )}

                {isSearching && (
                  <div className="mt-4 flex items-center justify-center md:mt-8 md:justify-between">
                    {isDesktop && (
                      <div className="flex w-fit items-center justify-center rounded-[20px] bg-darkGrey-200 px-6 py-4">
                        <HiSparkles
                          size={24}
                          className="me-4 text-darkGrey-700"
                        />
                        <span className="text-base font-medium text-secondaryGrey-800">
                          {translate("ns1:AI.SearchResults")}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-medium text-secondaryGrey-900 md:text-base">
                      <span className="font-bold">
                        {filteredTechniques.length}
                      </span>{" "}
                      {translate("ns1:AI.Results")}
                    </span>
                  </div>
                )}

                {filteredTechniques.length > 0 ? (
                  <div
                    className={`mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4 `}
                  >
                    {!isLoading ? (
                      <>
                        {filteredTechniques.map((technique, index) => (
                          <TechniqueCard
                            technique={technique}
                            key={"technique" + index}
                            onClick={() => openViewTechniqueModal(technique)}
                            selection
                            onSelect={() =>
                              updateSelectedTechniques(technique, "add")
                            }
                            selected={
                              selectedTechniques.filter(
                                (t) => technique?.id === t?.id
                              ).length > 0
                            }
                            favButton={false}
                            disableFlip
                          />
                        ))}
                      </>
                    ) : (
                      <>
                        {[...Array(12)].map((_, index) => (
                          <INSkeleton key={"techniques-skeleton" + index} />
                        ))}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-8 flex w-full flex-col items-center justify-center md:flex-row md:space-x-16">
                    <div className="w-full max-w-[250px]">
                      <NoResultFound />
                    </div>
                    <div className="px-2 text-center text-sm text-darkGrey-700 md:px-0 md:text-left md:text-base">
                      <Trans
                        i18nKey={
                          "ns1:CreateSeance.SelectTechniques.NoResultFound" as i18nKey
                        }
                        components={[<span className="font-extrabold" />]}
                      />
                    </div>
                  </div>
                )}
              </div>

              {!isDesktop && (
                <div className="mb-[64px]">
                  <MobileActivitiesFilters
                    filters={filters}
                    setFilters={setFilters}
                    fetchActivities={fetchTechniques}
                    activitiesType="techniques"
                    minDuration={minDuration}
                    maxDuration={maxDuration}
                    setIsFiltersOpen={setIsFiltersOpen}
                    resetFilters={resetFilters}
                    favoriteFilter={favoriteFilter}
                    setFavoriteFilter={setFavoriteFilter}
                  />
                </div>
              )}
            </div>

            {!isFiltersOpen && (
              <>
                <div className="sticky bottom-[78px] z-[75] max-h-dvh w-full">
                  <SelectionZone
                    selectedTechniques={selectedTechniques}
                    duration={selectedDuration}
                    updateSelectedTechniques={updateSelectedTechniques}
                    currentDuration={currentDuration}
                    isOpen={isSelectionZoneOpen}
                    setIsOpen={setIsSelectionZoneOpen}
                  />
                </div>

                <div className="sticky bottom-0 z-[100] flex w-dvw flex-col items-center bg-white px-6 py-4 shadow-lg">
                  <div className="w-full max-w-[590px]">
                    <PrimaryButton
                      type="submit"
                      height="46px"
                      width="100%"
                      onClick={addTechniquesToSeance}
                      disabled={selectedTechniques.length === 0}
                    >
                      {translate("ns1:CreateSeance.AddToSeance")}
                    </PrimaryButton>
                  </div>
                </div>
              </>
            )}
          </div>
        </ModalContent>
      </Modal>

      <ModalTechnique
        isOpen={modalViewTechnique.isOpen}
        partialTechnique={techniqueToView}
        closeViewTechniqueModal={closeViewTechniqueModal}
        selected={selectedTechniques.some((t) => t?.id === techniqueToView?.id)}
        selectedTechniques={selectedTechniques}
        setSelectedTechniques={setSelectedTechniques}
        updateSelectedTechniques={updateSelectedTechniques}
        setIsSelectionZoneOpen={setIsSelectionZoneOpen}
        onEdit={openEditTechniqueModal}
      />

      <ModalEditTechnique
        isOpen={modalEditTechnique.isOpen}
        partialTechnique={techniqueToView}
        closeEditTechniqueModal={closeEditTechniqueModal}
        updateSelectedTechniques={updateSelectedTechniques}
      />

      <ModalAISearch
        isOpen={modalAISearch.isOpen}
        onClose={modalAISearch.onClose}
        resetFilters={resetFilters}
      />
    </div>
  );
}
